import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import type { FormFieldMapping } from '../../types/fieldMapping';
import type { MatrixTolerance } from '@packages/matrix-solver';
import {
  solveMatrix,
  BREAKPOINTS,
  DEFAULT_MATRIX_COLUMN_GAP_PX,
  type Breakpoint,
  type CompiledCell,
  type MatrixFieldInput,
} from '@packages/matrix-solver';
import type { FormFieldsNode, FormSpacerEntry, FormSectionDividerEntry, FormInlineEachEntry, FormFieldStackEntry, FormActionButtonNode } from '../../types/layout';
import { FormFieldAdapter } from './FormFieldAdapter';
import { FormEachRenderer } from './FormEachRenderer';
import { FormActionButtonRenderer } from '../escape/FormActionButtonRenderer';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { useFormEngineDebug } from '../../context/FormEngineDebugContext';
import { useMatrixVisibility } from '../../hooks/useMatrixVisibility';
import { useMatrixContainerWidth } from '../../hooks/useMatrixContainerWidth';
import { MatrixFieldsRoot, MatrixFieldShell, MatrixSectionDivider, compileCellPlacement } from './MatrixFieldsMesh';
import { InlineUdfFields } from './InlineUdfFields';
import { MatrixColumnCountContext } from '../../context/MatrixColumnCountContext';
import { useDynamicFields } from '../../hooks/useDynamicFields';
import { DynamicFieldsSkeleton } from './DynamicFieldsSkeleton';
import { mergeMatrixFieldShellPresentation } from '../../utils/shellPresentation';
import type { ShellPresentation } from '../../types/fieldMapping';

interface InlineActionButtonEntry {
  type: 'inline-action-button';
  actionId: string;
  label: string;
  icon?: string;
  variant?: string;
  align?: string;
  labelByMode?: Record<string, string>;
  matrix?: MatrixTolerance;
  orchSectionId?: string;
  shellPresentation?: import('../../types/fieldMapping').ShellPresentation;
  hiddenWhen?: import('../../types/conditions').Condition;
  disabledWhen?: import('../../types/conditions').Condition;
  visibleInViewMode?: boolean;
  passFormData?: boolean;
  shellPresentation?: ShellPresentation;
}

type FieldEntry = FormFieldMapping | FormSpacerEntry | FormSectionDividerEntry | FormInlineEachEntry | FormFieldStackEntry | InlineActionButtonEntry;

const DEFAULT_TOLERANCE: MatrixTolerance = { minCols: 1, maxCols: 1 };

const INLINE_EACH_PLACEMENT_CSS = 'grid-column: 1 / -1;';

function isSpacerEntry(entry: FieldEntry): entry is FormSpacerEntry {
  return 'type' in entry && entry.type === 'spacer';
}

function isSectionDividerEntry(entry: FieldEntry): entry is FormSectionDividerEntry {
  return 'type' in entry && entry.type === 'section-divider';
}

function isInlineEachEntry(entry: FieldEntry): entry is FormInlineEachEntry {
  return 'type' in entry && entry.type === 'inline-each';
}

function isStackEntry(entry: FieldEntry): entry is FormFieldStackEntry {
  return 'type' in entry && entry.type === 'stack';
}

function isInlineActionButton(entry: FieldEntry): entry is InlineActionButtonEntry {
  return 'type' in entry && entry.type === 'inline-action-button';
}

function isNonFieldEntry(entry: FieldEntry): entry is FormSpacerEntry | FormSectionDividerEntry | FormInlineEachEntry | FormFieldStackEntry | InlineActionButtonEntry {
  return isSpacerEntry(entry) || isSectionDividerEntry(entry) || isInlineEachEntry(entry) || isStackEntry(entry) || isInlineActionButton(entry);
}

function fieldEntryId(entry: FieldEntry, idx: number): string {
  if (isInlineEachEntry(entry)) return `inline-each-${entry.node.name}-${idx}`;
  if (isStackEntry(entry)) return `stack-${entry.fields.map(f => f.name).join('-')}-${idx}`;
  if (isSectionDividerEntry(entry)) return `section-divider-${idx}`;
  if (isSpacerEntry(entry)) return `spacer-${idx}`;
  if (isInlineActionButton(entry)) return `inline-action-btn-${entry.actionId}-${idx}`;
  return entry.name;
}

/** @internal Exported for matrix layout contract tests. */
export function shouldIncludeInlineActionButtonInMatrix(
  visibilityMap: Record<string, boolean>,
  fieldId: string,
): boolean {
  return visibilityMap[fieldId] !== false;
}

function entryOrchSectionId(entry: FieldEntry): string | undefined {
  const id = (entry as { orchSectionId?: string }).orchSectionId;
  return typeof id === 'string' ? id : undefined;
}

function countsAsSectionChild(
  divider: FormSectionDividerEntry,
  entry: FieldEntry,
): boolean {
  if (divider.collapseWhenEmpty && divider.orchSectionId) {
    return entryOrchSectionId(entry) === divider.orchSectionId;
  }
  return true;
}

/** @internal Exported for section-collapse contract tests. */
export function computeHiddenSectionDividerIndexes(
  resolvedFields: FieldEntry[],
  visibilityMap: Record<string, boolean>,
): Set<number> {
  const hiddenDividers = new Set<number>();
  let currentDividerIdx = -1;
  let allChildrenHidden = true;
  let hasChildren = false;

  for (let i = 0; i < resolvedFields.length; i++) {
    const e = resolvedFields[i];
    if (isSectionDividerEntry(e)) {
      if (currentDividerIdx >= 0 && allChildrenHidden && hasChildren) {
        hiddenDividers.add(currentDividerIdx);
      }
      currentDividerIdx = i;
      allChildrenHidden = true;
      hasChildren = false;
    } else if (isInlineEachEntry(e)) {
      if (currentDividerIdx >= 0) {
        const divider = resolvedFields[currentDividerIdx] as FormSectionDividerEntry;
        if (countsAsSectionChild(divider, e)) {
          hasChildren = true;
          allChildrenHidden = false;
        }
      }
    } else if (!isSpacerEntry(e) && !isStaticHiddenField(e)) {
      if (currentDividerIdx < 0) continue;
      const divider = resolvedFields[currentDividerIdx] as FormSectionDividerEntry;
      if (!countsAsSectionChild(divider, e)) continue;
      hasChildren = true;
      const fId = fieldEntryId(e, i);
      if (visibilityMap[fId] !== false) {
        allChildrenHidden = false;
      }
    }
  }
  if (currentDividerIdx >= 0 && allChildrenHidden && hasChildren) {
    hiddenDividers.add(currentDividerIdx);
  }
  return hiddenDividers;
}

function resolveTolerance(
  entry: FieldEntry,
  layoutOverride?: { matrix?: MatrixTolerance },
): MatrixTolerance {
  if (isNonFieldEntry(entry)) return (entry as FormSpacerEntry).matrix ?? DEFAULT_TOLERANCE;
  return layoutOverride?.matrix ?? entry.matrix ?? DEFAULT_TOLERANCE;
}

function isStaticHiddenField(entry: FieldEntry): boolean {
  return !isNonFieldEntry(entry) && (entry as FormFieldMapping).hidden === true;
}

// ---------------------------------------------------------------------------
// Debug badge — inline to avoid circular package dependency on @packages/studio
// ---------------------------------------------------------------------------

type LimitState = '-' | '<' | '>' | '=';

interface DebugBadgeInfo {
  colState: LimitState;
  pxState: LimitState;
  actualCols: number;
  allocatedPx: number | null;
  tooltip: string;
}

function computeDebugBadge(
  tolerance: MatrixTolerance,
  cell: CompiledCell | undefined,
  columnCount: number,
  containerWidth: number | undefined,
  columnGapPx: number,
): DebugBadgeInfo | null {
  if (!cell || cell.isVirtualPlaceholder) return null;

  const actualCols = cell.gridSpan;
  const fMinCols = tolerance.minCols ?? 1;
  const fMaxCols = tolerance.maxCols ?? 1;

  let colState: LimitState = '-';
  if (actualCols <= fMinCols) colState = '<';
  else if (actualCols >= fMaxCols) colState = '>';

  const fMinPx = tolerance.minWidthPx ?? 0;
  const fMaxPx = tolerance.maxWidthPx ?? Infinity;

  let allocatedPx: number | null = null;
  let trackWidthPx: number | null = null;
  let pxState: LimitState = '-';

  if (containerWidth != null && containerWidth > 0 && columnCount > 0) {
    trackWidthPx = (containerWidth - (columnCount - 1) * columnGapPx) / columnCount;
    allocatedPx = Math.round(actualCols * trackWidthPx + (actualCols - 1) * columnGapPx);

    if (fMinPx === fMaxPx && fMinPx > 0) pxState = '=';
    else if (allocatedPx <= fMinPx + 2) pxState = '<';
    else if (fMaxPx !== Infinity && allocatedPx >= fMaxPx - 2) pxState = '>';
  }

  const maxPxLabel = fMaxPx === Infinity ? '\u221e' : String(fMaxPx);
  const containerLabel = containerWidth != null && containerWidth > 0 ? `${Math.round(containerWidth)}px` : '?';
  const trackLabel = trackWidthPx != null ? `${Math.round(trackWidthPx)}px` : '?';
  const tooltip = [
    `Container: ${containerLabel} \u00b7 ${columnCount} cols \u00b7 track ${trackLabel} \u00b7 gap ${columnGapPx}px`,
    `Px: ${pxState} (~${allocatedPx ?? '?'}px alloc | limits: ${fMinPx}-${maxPxLabel})`,
    `Colspan: ${colState} (${actualCols} cols | limits: ${fMinCols}-${fMaxCols})`,
  ].join('\n');

  return { colState, pxState, actualCols, allocatedPx, tooltip };
}

function FieldDebugBadge({ badge }: { badge: DebugBadgeInfo }): JSX.Element {
  return (
    <div style={{ marginBottom: 4 }}>
      <span
        title={badge.tooltip}
        style={{
          display: 'inline-block',
          fontSize: 10,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          background: '#f1f5f9',
          color: '#64748b',
          padding: '2px 6px',
          borderRadius: 4,
          border: '1px solid #e2e8f0',
          cursor: 'help',
          letterSpacing: '0.08em',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ color: '#94a3b8', fontFamily: 'inherit', fontSize: 8, marginRight: 2 }}>px</span>
        {badge.allocatedPx != null ? `${badge.allocatedPx}px \u00b7 ` : ''}
        {badge.pxState} : {badge.colState}
        <span style={{ color: '#94a3b8', fontFamily: 'inherit', fontSize: 8, marginLeft: 2 }}>col</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface FormFieldsRendererProps {
  node: FormFieldsNode;
  namePrefix?: string;
}

export const FormFieldsRenderer = React.memo(function FormFieldsRenderer({
  node,
  namePrefix,
}: FormFieldsRendererProps): React.ReactElement {
  const { form } = useFormEngineForm();
  const { modeOverrides, meta, getOptionProp } = useFormEngineState();
  const debug = useFormEngineDebug();

  const { fields: dynamicFields, loading: dynamicLoading } = useDynamicFields(
    node.dataSource?.actionId,
    node.dataSource?.params,
  );
  const resolvedFields = node.fields ?? dynamicFields;

  const visibilityMap = useMatrixVisibility({ fields: resolvedFields, namePrefix });
  const { containerRef, containerWidth } = useMatrixContainerWidth();

  const toleranceMap = useRef<Record<string, MatrixTolerance>>({});

  const solvedMatrix = useMemo(() => {
    const tolMap: Record<string, MatrixTolerance> = {};

    const logLines: string[] = [];
    const collectingLog = debug.enabled
      ? (msg: string) => { logLines.push(msg); }
      : undefined;

    const hiddenDividers = computeHiddenSectionDividerIndexes(resolvedFields, visibilityMap);

    const matrixFields: MatrixFieldInput[] = resolvedFields.flatMap<MatrixFieldInput>((entry, idx) => {
      if (isStaticHiddenField(entry)) return [];

      // inline-each entries occupy a full-width row in the grid.
      // Represent them as row-break markers so the solver tracks their row
      // position and subsequent fields are placed on the correct row.
      if (isInlineEachEntry(entry)) {
        const id = fieldEntryId(entry, idx);
        tolMap[id] = DEFAULT_TOLERANCE;
        return [{
          id,
          order: idx,
          tolerance: DEFAULT_TOLERANCE,
          visible: Object.fromEntries(BREAKPOINTS.map((bp) => [bp, true])) as Partial<Record<Breakpoint, boolean>>,
          kind: 'row-break' as const,
        }];
      }

      if (isStackEntry(entry)) {
        const id = fieldEntryId(entry, idx);
        const tol = entry.matrix ?? { minCols: 2, maxCols: 3 };
        tolMap[id] = tol;
        return [{
          id,
          order: idx,
          tolerance: tol,
          visible: Object.fromEntries(BREAKPOINTS.map((bp) => [bp, true])) as Partial<Record<Breakpoint, boolean>>,
          kind: 'field' as const,
        }];
      }

      if (isInlineActionButton(entry)) {
        const id = fieldEntryId(entry, idx);
        if (!shouldIncludeInlineActionButtonInMatrix(visibilityMap, id)) return [];
        const tol = entry.matrix ?? DEFAULT_TOLERANCE;
        tolMap[id] = tol;
        const isVisible = visibilityMap[id] ?? true;
        return [{
          id,
          order: idx,
          tolerance: tol,
          visible: Object.fromEntries(
            BREAKPOINTS.map((bp) => [bp, isVisible]),
          ) as Partial<Record<Breakpoint, boolean>>,
          kind: 'field' as const,
        }];
      }

      const isDivider = isSectionDividerEntry(entry);
      const isSpacer = isSpacerEntry(entry);
      const id = fieldEntryId(entry, idx);

      if (isDivider) {
        if (hiddenDividers.has(idx)) return [];
        const dividerKind = entry.groupHint ? 'group-hint' as const : 'row-break' as const;
        tolMap[id] = DEFAULT_TOLERANCE;
        return [
          {
            id,
            order: idx,
            tolerance: DEFAULT_TOLERANCE,
            visible: Object.fromEntries(
              BREAKPOINTS.map((bp) => [bp, true]),
            ) as Partial<Record<Breakpoint, boolean>>,
            kind: dividerKind,
          },
        ];
      }

      if (!isSpacer && visibilityMap[id] === false) return [];

      const layoutOverride = !isSpacer ? modeOverrides?.fieldLayout?.[entry.name] : undefined;
      const tol = resolveTolerance(entry, layoutOverride);
      tolMap[id] = tol;

      const effectiveOrder = isSpacer ? idx : (layoutOverride?.order ?? entry.order ?? idx);
      const effectiveColStart = isSpacer ? undefined : layoutOverride?.colStart ?? entry.colStart;

      return [
        {
          id,
          order: effectiveOrder,
          tolerance: tol,
          start: effectiveColStart
            ? (Object.fromEntries(BREAKPOINTS.map((bp) => [bp, effectiveColStart!])) as Partial<
                Record<Breakpoint, number>
              >)
            : undefined,
          visible: Object.fromEntries(
            BREAKPOINTS.map((bp) => [bp, visibilityMap[id] ?? true]),
          ) as Partial<Record<Breakpoint, boolean>>,
          kind: isSpacer ? ('spacer' as const) : ('field' as const),
        },
      ];
    });

    toleranceMap.current = tolMap;

    const standardVisibility = Object.fromEntries(
      BREAKPOINTS.map((bp) => [bp, visibilityMap]),
    ) as Partial<Record<Breakpoint, Record<string, boolean>>>;

    const result = solveMatrix({
      row: { id: 'form_fields_mesh', fields: matrixFields, columns: node.columns },
      visibility: standardVisibility,
      options: {
        preserveHiddenFootprint: false,
        responsiveDownshift: true,
        containerWidth,
        columnGapPx: DEFAULT_MATRIX_COLUMN_GAP_PX,
        log: collectingLog ?? (import.meta.env.DEV ? console.debug : undefined),
      },
    });

    if (debug.enabled && logLines.length > 0) {
      debug.logCollector.current = logLines;
    }

    return result;
  }, [resolvedFields, node.columns, visibilityMap, modeOverrides, containerWidth, debug.enabled]);

  const columnCount = solvedMatrix.base.columnCount;

  const cellMap = useMemo(() => {
    const map: Record<string, Partial<Record<Breakpoint, CompiledCell>>> = {};
    BREAKPOINTS.forEach((bp) => {
      solvedMatrix[bp].cells.forEach((cell) => {
        if (!map[cell.id]) map[cell.id] = {};
        map[cell.id][bp] = cell;
      });
    });
    return map;
  }, [solvedMatrix]);

  const maxGridRow = useMemo(() => {
    let max = 0;
    for (const cell of solvedMatrix.base.cells) {
      if (cell.gridRowStart > max) max = cell.gridRowStart;
    }
    return max;
  }, [solvedMatrix]);

  const onTelemetryRef = React.useRef(debug.onTelemetry);
  onTelemetryRef.current = debug.onTelemetry;

  useEffect(() => {
    if (!debug.enabled || !onTelemetryRef.current) return;
    let trackWidthPx: number | null = null;
    if (containerWidth != null && containerWidth > 0 && columnCount > 0) {
      trackWidthPx = (containerWidth - (columnCount - 1) * DEFAULT_MATRIX_COLUMN_GAP_PX) / columnCount;
    }
    onTelemetryRef.current({
      containerWidth,
      columnCount,
      columnGapPx: DEFAULT_MATRIX_COLUMN_GAP_PX,
      trackWidthPx,
      solverLogs: debug.logCollector.current,
    });
  }, [debug.enabled, containerWidth, columnCount]);

  const showBadges = debug.enabled && debug.showDebugBadges;
  const showCards = debug.enabled && debug.showCards;

  // Section-lead / standalone-header flags drive spacing on MatrixFieldsRoot.
  // Lead margin is applied only when this grid is NOT :first-child (see MatrixFieldsMesh) —
  // first blocks must not invent a 32px gap under the page title or card chrome.
  const startsWithDivider =
    resolvedFields.length > 0 && isSectionDividerEntry(resolvedFields[0]);

  const isStandaloneHeaderBlock =
    startsWithDivider && resolvedFields.every(isSectionDividerEntry);

  let sectionDividerCount = 0;

  const showHeaderRow = node.showHeaders && resolvedFields.length > 0;
  const headerLabels = useMemo(() => {
    if (!showHeaderRow) return [];
    const labels: string[] = [];
    let started = false;
    for (const entry of resolvedFields) {
      if (isSectionDividerEntry(entry)) {
        if (started) break;
        started = true;
        continue;
      }
      if (!isNonFieldEntry(entry) && !isStaticHiddenField(entry)) {
        labels.push((entry as FormFieldMapping).def?.label ?? '');
      }
    }
    return labels;
  }, [showHeaderRow, resolvedFields]);

  if (node.dataSource && dynamicLoading && resolvedFields.length === 0) {
    return <DynamicFieldsSkeleton />;
  }

  return (
    <MatrixColumnCountContext.Provider value={columnCount}>
    {node.title && (
      <div style={{ fontSize: 14, fontWeight: 600, padding: '12px 0 4px', color: '#334155' }}>
        {node.title}
      </div>
    )}
    {showHeaderRow && headerLabels.length > 0 && (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
          gap: `0 ${DEFAULT_MATRIX_COLUMN_GAP_PX}px`,
          padding: '8px 0 4px',
          borderBottom: '1px solid #e2e8f0',
          marginBottom: 8,
        }}
      >
        {headerLabels.map((label, i) => (
          <div key={i} style={{ fontSize: 11, fontWeight: 500, color: '#64748b', textAlign: 'center' }}>
            {label}
          </div>
        ))}
      </div>
    )}
    <MatrixFieldsRoot
      ref={containerRef}
      columnCount={columnCount}
      sectionLead={startsWithDivider && !isStandaloneHeaderBlock}
      standaloneHeader={isStandaloneHeaderBlock}
      data-row-separator={node.rowSeparator ? 'true' : undefined}
      style={node.rootStyle as React.CSSProperties | undefined}
    >
      {resolvedFields.map((entry, idx) => {
        const id = fieldEntryId(entry, idx);

        if (isStaticHiddenField(entry)) return null;

        if (isInlineEachEntry(entry)) {
          // Use the solver-assigned grid-row so the each section lands at the
          // correct vertical position. Force full-width span regardless.
          const bpCells = cellMap[id];
          const placementCSS = bpCells
            ? compileCellPlacement(cellMap, id)
            : INLINE_EACH_PLACEMENT_CSS;
          return (
            <MatrixFieldShell
              key={id}
              $placementCSS={placementCSS}
              $isVirtual={false}
            >
              <FormEachRenderer node={entry.node} />
            </MatrixFieldShell>
          );
        }

        if (isSectionDividerEntry(entry)) {
          const bpCells = cellMap[id];
          if (!bpCells) return null;

          const isFirst = sectionDividerCount === 0;
          sectionDividerCount++;

          const placementCSS = compileCellPlacement(cellMap, id);
          return (
            <MatrixSectionDivider
              key={id}
              title={entry.title}
              subtitle={entry.subtitle}
              titleVariant={entry.titleVariant}
              divider={entry.divider}
              semanticId={entry.semanticId}
              placementCSS={placementCSS}
              isFirst={isFirst}
              titleStyle={entry.titleStyle}
              lozengeVariant={entry.lozengeVariant}
              titleSpacing={entry.titleSpacing}
            />
          );
        }

        if (isStackEntry(entry)) {
          const bpCells = cellMap[id];
          if (!bpCells) return null;
          const placementCSS = compileCellPlacement(cellMap, id);
          const gap = entry.gap ?? 4;
          return (
            <MatrixFieldShell
              key={id}
              $placementCSS={placementCSS}
              $isVirtual={false}
            >
              <div className={entry.className} style={{ display: 'flex', flexDirection: 'column', gap, alignSelf: 'end', paddingBottom: 10, ...entry.style }}>
                {entry.fields.map((subField) => {
                  const isSubSelected = debug.enabled && debug.selectedFieldName === subField.name;
                  const isSubClickable = debug.enabled && Boolean(debug.onFieldClick);
                  return (
                    <div
                      key={subField.name}
                      role={isSubClickable ? 'button' : undefined}
                      tabIndex={isSubClickable ? 0 : undefined}
                      onClick={isSubClickable ? (e) => { e.stopPropagation(); debug.onFieldClick!(subField.name); } : undefined}
                      onKeyDown={isSubClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); debug.onFieldClick!(subField.name); } } : undefined}
                      style={isSubClickable ? {
                        cursor: 'pointer',
                        borderRadius: 6,
                        outline: isSubSelected ? '2px solid #4f46e5' : '2px solid transparent',
                        outlineOffset: 3,
                        background: isSubSelected ? 'rgba(79,70,229,0.04)' : undefined,
                        transition: 'outline-color 0.15s ease',
                        padding: isSubSelected ? '2px 4px' : undefined,
                      } : undefined}
                    >
                      <FormFieldAdapter field={subField} namePrefix={namePrefix} inStack />
                    </div>
                  );
                })}
              </div>
            </MatrixFieldShell>
          );
        }

        if (isInlineActionButton(entry)) {
          const bpCells = cellMap[id];
          if (!bpCells) return null;
          const isBtnHidden = visibilityMap[id] === false;
          const placementCSS = compileCellPlacement(cellMap, id);
          const btnNode: FormActionButtonNode = {
            type: 'action-button',
            actionId: entry.actionId,
            label: entry.label,
            ...(entry.icon != null && { icon: entry.icon }),
            ...(entry.variant != null && { variant: entry.variant }),
            ...(entry.align != null && { align: entry.align as 'start' | 'end' }),
            ...(entry.labelByMode != null && { labelByMode: entry.labelByMode }),
            ...(entry.hiddenWhen != null && { hiddenWhen: entry.hiddenWhen }),
            ...(entry.disabledWhen != null && { disabledWhen: entry.disabledWhen }),
            ...(entry.visibleInViewMode != null && { visibleInViewMode: entry.visibleInViewMode }),
            ...(entry.passFormData === true && { passFormData: true }),
          };
          const isOwnRow = entry.matrix?.startNewRow === true;
          const isFullWidthRow = (entry.matrix?.maxCols ?? 0) >= 12;
          // When the button shares a grid row with a labeled field, pad the top
          // by the label-row height (26px — matches FormField LabelRow min-height)
          // so the button aligns with the sibling's input control. Without this,
          // the button sits at the top of the cell and drifts when validation
          // errors expand the row height. Buttons on their own row (startNewRow)
          // need no padding since there is no adjacent label to account for.
          const rowAlignStyle: React.CSSProperties | undefined =
            isBtnHidden || entry.align !== 'end'
              ? undefined
              : isFullWidthRow
                ? { width: '100%', maxWidth: '100%', justifySelf: 'stretch' }
                : { justifySelf: 'end' };
          const labelRowPad: React.CSSProperties | undefined =
            isBtnHidden || isOwnRow ? undefined : { paddingTop: 26 };
          const { style: mergedShellStyle, className: shellClassName } = mergeMatrixFieldShellPresentation({
            shellPresentation: entry.shellPresentation,
            alignStyle: rowAlignStyle,
            skipPresentation: isBtnHidden,
          });
          const shellStyle: React.CSSProperties | undefined =
            mergedShellStyle || labelRowPad
              ? { ...mergedShellStyle, ...labelRowPad }
              : undefined;
          return (
            <MatrixFieldShell
              key={id}
              $placementCSS={placementCSS}
              $isVirtual={isBtnHidden}
              aria-hidden={isBtnHidden ? true : undefined}
              className={shellClassName}
              style={shellStyle}
            >
              {!isBtnHidden && <FormActionButtonRenderer node={btnNode} />}
            </MatrixFieldShell>
          );
        }

        const isSpacer = isSpacerEntry(entry);
        const bpCells = cellMap[id];

        if (!bpCells) return null;

        const isVirtual = !(visibilityMap[id] ?? true);
        const placementCSS = compileCellPlacement(cellMap, id);

        const badge = showBadges && !isVirtual
          ? computeDebugBadge(
              toleranceMap.current[id] ?? DEFAULT_TOLERANCE,
              bpCells.base,
              columnCount,
              containerWidth,
              DEFAULT_MATRIX_COLUMN_GAP_PX,
            )
          : null;

        const isField = !isVirtual && !isSpacer;
        const isSelected = debug.enabled && isField && debug.selectedFieldName === id;
        const isClickable = debug.enabled && isField && Boolean(debug.onFieldClick);

        const cardStyle: React.CSSProperties | undefined = showCards && isField
          ? {
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              padding: 12,
            }
          : undefined;

        const selectionStyle: React.CSSProperties | undefined = isClickable
          ? {
              cursor: 'pointer',
              borderRadius: 8,
              outline: isSelected ? '2px solid #4f46e5' : '2px solid transparent',
              outlineOffset: 2,
              background: isSelected ? 'rgba(79,70,229,0.04)' : cardStyle?.background,
              transition: 'outline-color 0.15s ease',
            }
          : undefined;

        const alignStyle: React.CSSProperties | undefined = undefined;

        const debugStyle: React.CSSProperties | undefined =
          cardStyle || selectionStyle
            ? { ...cardStyle, ...selectionStyle }
            : undefined;

        const fieldMapping = isField ? (entry as FormFieldMapping) : undefined;
        const { style: mergedStyle, className: shellClassName } = mergeMatrixFieldShellPresentation({
          shellPresentation: fieldMapping?.shellPresentation,
          alignStyle,
          debugStyle,
          skipPresentation: isVirtual || isSpacer,
        });

        const handleClick = isClickable
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              debug.onFieldClick!(id);
            }
          : undefined;

        const handleKeyDown = isClickable
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                debug.onFieldClick!(id);
              }
            }
          : undefined;

        return (
          <MatrixFieldShell
            key={id}
            $placementCSS={placementCSS}
            $isVirtual={isVirtual}
            aria-hidden={isVirtual ? true : undefined}
            className={shellClassName}
            style={mergedStyle}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            title={isClickable ? 'Click to inspect field' : undefined}
          >
            {badge && <FieldDebugBadge badge={badge} />}
            {isField && (
              <FormFieldAdapter field={entry as FormFieldMapping} namePrefix={namePrefix} />
            )}
          </MatrixFieldShell>
        );
      })}
      {node.inlineUdfNodes?.map((udfNode, udfIdx) => (
        <InlineUdfFields
          key={`inline-udf-${udfIdx}`}
          node={udfNode}
          namePrefix={namePrefix}
          columnCount={columnCount}
          sectionDividerIndex={sectionDividerCount + udfIdx}
          gridRowStart={maxGridRow + 1 + udfIdx * 2}
        />
      ))}
    </MatrixFieldsRoot>
    </MatrixColumnCountContext.Provider>
  );
});
